import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import axios from 'axios';
import AddPolicyDialog from '../components/AddPolicyDialog';
import AddClientDialog from '../components/AddClientDialog';
import AddProductDialog from '../components/AddProductDialog';
import ClientLeadPickerDialog from '../components/ClientLeadPickerDialog';

const GlobalDialogsContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useGlobalDialogs = () => {
  const ctx = useContext(GlobalDialogsContext);
  if (!ctx) throw new Error('useGlobalDialogs must be used within GlobalDialogsProvider');
  return ctx;
};

/**
 * Provides app-wide "Add Client", "Add Policy", and "Add Product" access.
 * Also exposes a client/lead picker used by Dashboard Quick Actions so
 * every quick-added product is linked to an existing person.
 */
export const GlobalDialogsProvider = ({ children }) => {
  const [policyDialog, setPolicyDialog] = useState({ open: false, clientId: '', productType: '' });
  const [productDialog, setProductDialog] = useState({ open: false, clientId: '', category: '' });
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [pickerState, setPickerState] = useState({ open: false, onPick: null, title: '', description: '' });
  const [clients, setClients] = useState([]);
  const [leads, setLeads] = useState([]);
  const [policyCreatedListeners, setPolicyCreatedListeners] = useState([]);
  const [clientCreatedListeners, setClientCreatedListeners] = useState([]);

  // Lazy-load clients + leads once (used by client/lead picker)
  useEffect(() => {
    axios.get(`${API}/clients`, { withCredentials: true }).then(r => setClients(r.data)).catch(() => {});
    axios.get(`${API}/leads`, { withCredentials: true }).then(r => setLeads(r.data)).catch(() => {});
  }, []);

  const openAddPolicy = useCallback(({ clientId = '', productType = '' } = {}) => {
    setPolicyDialog({ open: true, clientId, productType });
  }, []);
  const closeAddPolicy = useCallback(() => setPolicyDialog((s) => ({ ...s, open: false })), []);

  const openAddProduct = useCallback(({ clientId = '', category = '' } = {}) => {
    setProductDialog({ open: true, clientId, category });
  }, []);
  const closeAddProduct = useCallback(() => setProductDialog((s) => ({ ...s, open: false })), []);

  const openAddClient = useCallback(() => setClientDialogOpen(true), []);
  const closeAddClient = useCallback(() => setClientDialogOpen(false), []);

  const openClientLeadPicker = useCallback(({ onPick, title, description } = {}) => {
    setPickerState({ open: true, onPick, title: title || 'Select client or lead', description: description || '' });
  }, []);
  const closeClientLeadPicker = useCallback(() => setPickerState((s) => ({ ...s, open: false, onPick: null })), []);

  const onPolicyCreated = useCallback((listener) => {
    setPolicyCreatedListeners((prev) => [...prev, listener]);
    return () => setPolicyCreatedListeners((prev) => prev.filter((l) => l !== listener));
  }, []);
  const firePolicyCreated = useCallback((policy) => {
    policyCreatedListeners.forEach((l) => { try { l(policy); } catch (e) { console.warn('policyCreated listener error', e); } });
  }, [policyCreatedListeners]);

  const onClientCreated = useCallback((listener) => {
    setClientCreatedListeners((prev) => [...prev, listener]);
    return () => setClientCreatedListeners((prev) => prev.filter((l) => l !== listener));
  }, []);
  const fireClientCreated = useCallback((client) => {
    clientCreatedListeners.forEach((l) => { try { l(client); } catch (e) { console.warn('clientCreated listener error', e); } });
    // Refresh cached list so pickers see the new client
    axios.get(`${API}/clients`, { withCredentials: true }).then(r => setClients(r.data)).catch(() => {});
  }, [clientCreatedListeners]);

  const value = useMemo(
    () => ({
      openAddPolicy, openAddClient, openAddProduct, openClientLeadPicker,
      onPolicyCreated, onClientCreated,
      clients, leads,
    }),
    [openAddPolicy, openAddClient, openAddProduct, openClientLeadPicker, onPolicyCreated, onClientCreated, clients, leads]
  );

  const handlePickerPick = (picked) => {
    const cb = pickerState.onPick;
    closeClientLeadPicker();
    if (cb) cb(picked);
  };

  return (
    <GlobalDialogsContext.Provider value={value}>
      {children}
      <AddClientDialog
        open={clientDialogOpen}
        onClose={closeAddClient}
        onCreated={fireClientCreated}
        onRequestFollowUpPolicy={(clientId) => openAddPolicy({ clientId })}
      />
      <AddPolicyDialog
        open={policyDialog.open}
        onClose={closeAddPolicy}
        initialClientId={policyDialog.clientId}
        initialProductType={policyDialog.productType}
        onCreated={firePolicyCreated}
      />
      <AddProductDialog
        open={productDialog.open}
        onClose={closeAddProduct}
        initialClientId={productDialog.clientId}
        initialCategory={productDialog.category}
        onCreated={firePolicyCreated}
        onRequestInsurance={({ clientId }) => openAddPolicy({ clientId })}
      />
      <ClientLeadPickerDialog
        open={pickerState.open}
        onClose={closeClientLeadPicker}
        clients={clients}
        leads={leads}
        onPick={handlePickerPick}
        title={pickerState.title}
        description={pickerState.description}
      />
    </GlobalDialogsContext.Provider>
  );
};
